import { Form, Input, Button, Typography, Carousel, Select, Modal } from "antd";
import FormItem from "antd/lib/form/FormItem";
import { useCallback, useRef, useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import {
  getIsSSOLogin,
  login,
  setAccessToken,
  setCurrentSubscriptionId,
  setIDToken,
  setRefreshToken
} from "services/auth";
import { ErrorMessages } from "constants/index";
import { AppLogoIcon } from "components/svg-icons";
import { LoginRequest } from "models/user";
import PiiConsent from "components/widgets/pii-consent";
import { ArrowLeftOutlined } from "@ant-design/icons";
import jwtDecode from "jwt-decode";

const { Title } = Typography;

function Login() {
  const history = useHistory();
  const location = useLocation();
  const redirectUrl = new URLSearchParams(location.search).get("redirectUrl");
  const emailFromSso = new URLSearchParams(location.search).get("email");

  const [loader, setLoader] = useState(false);
  const [loginForm] = Form.useForm();
  const [subscriptionForm] = Form.useForm();

  const [loginInfo, setloginInfo] = useState<any>(null);
  const [userSubscriptions, setUserSubscriptions] = useState<any[]>([]);

  const slider: any = useRef();

  useEffect(() => {
    const isSSOLogin = getIsSSOLogin();
    if (isSSOLogin) {
      history.replace("/projects");
    }
  }, [history]);

  useEffect(() => {
    if (emailFromSso) {
      loginForm.setFieldsValue({
        email: emailFromSso
      });
    }
  }, [emailFromSso, loginForm]);

  const onLoginFinish = useCallback(
    async (event: LoginRequest) => {
      setLoader(true);
      const emailToUse = event.email || emailFromSso || "";
      const res: any = await login({
        email: emailToUse,
        password: event.password
      });

      if (res.status === 200) {
        setIDToken(res?.data.success.id_token);
        setAccessToken(res?.data.success.access_token);
        setRefreshToken(res?.data.success.refresh_token);

        if (res.data.success.is_password_expired) {
          history.push("/reset_expired_password");
          return;
        }

        const decodedToken: any = jwtDecode(res?.data.success.access_token);
        const subscriptions = decodedToken?.associated_subscriptions || [];

        const loginDetails = {
          token: res?.data.success.access_token,
          is_password_expired: res.data.success.is_password_expired,
          associated_subscriptions: subscriptions
        };
        setloginInfo(loginDetails);

        if (subscriptions.length === 1) {
          // single subscriptions
          setCurrentSubscriptionId(subscriptions[0].id);
          if (redirectUrl) history.push(redirectUrl);
          else history.push("/projects");
          return;
        }
        loginForm.resetFields();
        const subArr = subscriptions.sort((subA: any, subB: any) =>
          subA?.name?.toLowerCase() > subB?.name?.toLowerCase() ? 1 : -1
        );
        setUserSubscriptions(subArr);
        subscriptionForm.setFieldsValue({
          subscriptionId: subArr[0].id
        });
        setLoader(false);
        slider.current.next();
      } else if (res.response?.status === 401) {
        setLoader(false);
        history.push(
          `/?error=${encodeURIComponent(
            res.response?.data?.error
          )}&email=${encodeURIComponent(emailToUse)}`
        );
      } else if (res.response?.status === 403) {
        setLoader(false);
        history.push(`/account-locked?email=${encodeURIComponent(emailToUse)}`);
      } else {
        setLoader(false);
        Modal.error({ content: res.response?.data?.error || res.message });
      }
    },
    [history, loginForm, redirectUrl, subscriptionForm, emailFromSso]
  );

  const goBackToLogin = () => {
    history.push("/");
  };

  return (
    <div className="login">
      <div className="login-form">
        <div className="flex items-center gap-1 mb-2">
          <Button
            icon={
              <ArrowLeftOutlined style={{ color: "#000000", opacity: "45%" }} />
            }
            className="text-one text-black !bg-transparent !border-0 px-0 !shadow-none"
            onClick={goBackToLogin}
          />
          <div>{emailFromSso}</div>
        </div>
        <div style={{ margin: "auto", display: "block" }}>
          <AppLogoIcon />
        </div>
        <div className="login-logo">
          <Title level={3} className="mt-3">
            Login
          </Title>
        </div>
        <Carousel
          dots={false}
          afterChange={() => {}}
          ref={(ref) => {
            slider.current = ref;
          }}
          speed={250}
          infinite={false}
          swipe={false}
          swipeToSlide={false}
          draggable={false}
        >
          <div className="px-1">
            <Form
              onFinish={onLoginFinish}
              form={loginForm}
              labelCol={{ span: 50 }}
              wrapperCol={{ span: 30 }}
              scrollToFirstError
              layout="vertical"
            >
              <FormItem
                name="password"
                label="Password"
                className="password"
                validateStatus=""
                rules={[
                  {
                    required: true,
                    validateTrigger: "onSubmit",
                    message: ErrorMessages.PasswordEmptyCheck
                  }
                ]}
              >
                <Input.Password
                  placeholder="Enter Password"
                  tabIndex={loginInfo ? -1 : 0}
                />
              </FormItem>
              <div className="flex justify-end items-center w-full mt-2">
                <a
                  href="/forgot_password"
                  className="text-gray-800"
                  tabIndex={loginInfo ? -1 : 0}
                >
                  Forgot password?
                </a>
              </div>
              <div className="pt-4">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  loading={loader}
                  disabled={loader}
                  tabIndex={loginInfo ? -1 : 0}
                  className="w-full"
                >
                  Login
                </Button>
              </div>
            </Form>
          </div>
          <div className="px-1">
            <Form
              onFinish={(subscriptionData: any) => {
                setCurrentSubscriptionId(subscriptionData.subscriptionId);
                if (redirectUrl) history.push(redirectUrl);
                else history.push("/projects");
              }}
              form={subscriptionForm}
              labelCol={{ span: 50 }}
              wrapperCol={{ span: 30 }}
              scrollToFirstError
              layout="vertical"
              className="-mt-10"
            >
              <FormItem name="subscriptionId" label="Choose a subscription">
                <Select
                  showSearch
                  optionFilterProp="label"
                  className="w-full"
                  onChange={() => {}}
                  options={userSubscriptions.map((sub: any) => {
                    return {
                      value: sub.id,
                      label: sub.name
                    };
                  })}
                  tabIndex={!loginInfo ? -1 : 0}
                />
              </FormItem>

              <div
                style={{ paddingTop: "1em" }}
                className="w-full grid grid-cols-3 content-center"
              >
                <div className="flex items-center">
                  {/* <Button
                      type="link"
                      size="small"
                      className="m-0 p-0"
                      onClick={() => {
                        slider.current.prev();
                      }}
                    >
                      <LeftOutlined />
                      <span className="!ml-0">Back</span>
                    </Button> */}
                </div>
                <div className="flex justify-center w-full pb-3">
                  <Button
                    htmlType="submit"
                    size="middle"
                    loading={loader}
                    disabled={loader}
                    tabIndex={!loginInfo ? -1 : 0}
                    className="w-full"
                  >
                    Continue
                  </Button>
                </div>
                <div />
              </div>
            </Form>
          </div>
        </Carousel>
      </div>
      <PiiConsent className="w-[350px] text-center text-xs text-ciq-gray-470 pt-7" />
    </div>
  );
}

export default Login;
